import { AnyAction } from 'redux';
import {
  GET_INTERMEDIARIES,
  GET_INTERMEDIARIES_FAILED,
  GET_INTERMEDIARIES_SUCCESS,
  GET_INTERMEDIARY_TO_DELETE_APPLICATIONS,
  GET_INTERMEDIARY_TO_DELETE_APPLICATIONS_FAILURE,
  GET_INTERMEDIARY_TO_DELETE_APPLICATIONS_SUCCESS,
  LoanOriginationSystemIntermediariesActionType,
  SORT_INTERMEDIARIES,
} from './ActionTypes';
import { TableViewData } from 'api/LoanOriginationSystem/Types';
import FailedActionCreator from 'utils/actions/FailedActionCreator';
import { LoanOriginationSystemIntermediariesFiltersState } from './Filters/Types';
import {
  IntermediariesSortingField,
  IntermediariesSortingType,
  Intermediary,
} from 'api/LoanOriginationSystem/LoanOriginationSystemIntermediariesApi';
import { SimplifiedApplication } from 'api/LoanOriginationSystem/SimplifiedApplicationsApi';

type GetIntermediariesSuccessData = TableViewData<Intermediary> & { searchInputValue: string };
export type GetIntermediaryToDeleteApplicationsSuccessData = TableViewData<SimplifiedApplication>;

export interface GetIntermediariesAction extends AnyAction {
  type: typeof GET_INTERMEDIARIES;
  payload: {
    filters: LoanOriginationSystemIntermediariesFiltersState;
    sortingType: IntermediariesSortingType;
    abortSignal: AbortSignal;
  };
}

export interface GetIntermediariesSuccessAction extends AnyAction {
  type: typeof GET_INTERMEDIARIES_SUCCESS;
  payload: GetIntermediariesSuccessData;
}

export interface SortIntermediariesAction extends AnyAction {
  type: typeof SORT_INTERMEDIARIES;
  payload: IntermediariesSortingType;
}

export interface DeleteIntermediaryAction extends AnyAction {
  type: LoanOriginationSystemIntermediariesActionType.DeleteIntermediary;
  payload: {
    intermediaryId: string;
    intermediaryName: string;
  };
}

export interface DeleteIntermediarySuccessAction extends AnyAction {
  type: LoanOriginationSystemIntermediariesActionType.DeleteIntermediarySuccess;
  payload: {
    intermediary: Intermediary;
  };
}

export interface DeleteIntermediaryFailureAction extends AnyAction {
  type: LoanOriginationSystemIntermediariesActionType.DeleteIntermediaryFailure;
  payload: Error;
}

export interface GetIntermediaryToDeleteApplicationsAction extends AnyAction {
  type: typeof GET_INTERMEDIARY_TO_DELETE_APPLICATIONS;
  payload: {
    id: string;
  };
}

export interface GetIntermediaryToDeleteApplicationsSuccessAction extends AnyAction {
  type: typeof GET_INTERMEDIARY_TO_DELETE_APPLICATIONS_SUCCESS;
  payload: {
    applications: GetIntermediaryToDeleteApplicationsSuccessData;
  };
}

export interface SetIntermediaryToDeleteAction extends AnyAction {
  type: LoanOriginationSystemIntermediariesActionType.SetIntermediaryToDelete;
  payload: {
    intermediary: Intermediary | null;
  };
}

let getIntermediariesAbortController: AbortController | null = null;
export const getIntermediaries = (
  filters: LoanOriginationSystemIntermediariesFiltersState,
  sortingType: IntermediariesSortingType,
): GetIntermediariesAction => {
  if (getIntermediariesAbortController) {
    getIntermediariesAbortController.abort();
  }

  getIntermediariesAbortController = new AbortController();

  return {
    type: GET_INTERMEDIARIES,
    payload: {
      filters,
      sortingType,
      abortSignal: getIntermediariesAbortController.signal,
    },
  };
};

export const getIntermediariesSuccess = (result: GetIntermediariesSuccessData): GetIntermediariesSuccessAction => {
  return {
    type: GET_INTERMEDIARIES_SUCCESS,
    payload: result,
  };
};

export const sortIntermediaries = (
  field: IntermediariesSortingField,
  ascending: boolean,
): SortIntermediariesAction => ({
  type: SORT_INTERMEDIARIES,
  payload: {
    ascending,
    field,
  },
});

export const setIntermediaryToDelete = (intermediary: Intermediary | null): SetIntermediaryToDeleteAction => {
  return {
    type: LoanOriginationSystemIntermediariesActionType.SetIntermediaryToDelete,
    payload: {
      intermediary,
    },
  };
};

export const deleteIntermediary = (intermediaryId: string, intermediaryName: string): DeleteIntermediaryAction => {
  return {
    type: LoanOriginationSystemIntermediariesActionType.DeleteIntermediary,
    payload: {
      intermediaryId,
      intermediaryName,
    },
  };
};

export const deleteIntermediarySuccess = (intermediary: Intermediary): DeleteIntermediarySuccessAction => {
  return {
    type: LoanOriginationSystemIntermediariesActionType.DeleteIntermediarySuccess,
    payload: {
      intermediary,
    },
  };
};

export const getIntermediaryToDeleteApplications = (id: string): GetIntermediaryToDeleteApplicationsAction => {
  return {
    type: GET_INTERMEDIARY_TO_DELETE_APPLICATIONS,
    payload: {
      id,
    },
  };
};

export const getIntermediaryToDeleteApplicationsSuccess = (
  applications: GetIntermediaryToDeleteApplicationsSuccessData,
): GetIntermediaryToDeleteApplicationsSuccessAction => {
  return {
    type: GET_INTERMEDIARY_TO_DELETE_APPLICATIONS_SUCCESS,
    payload: {
      applications,
    },
  };
};

export const deleteIntermediaryFailure = (error: Error): DeleteIntermediaryFailureAction => ({
  type: LoanOriginationSystemIntermediariesActionType.DeleteIntermediaryFailure,
  payload: error,
});

export const getIntermediaryToDeleteApplicationsFailed = FailedActionCreator(
  GET_INTERMEDIARY_TO_DELETE_APPLICATIONS_FAILURE,
);

export const getIntermediariesFailed = FailedActionCreator(GET_INTERMEDIARIES_FAILED);
